import { useState, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { formatNumber, formatGap, getGapColor, chfToPresentation } from '../lib/utils';
import { cn } from '../lib/utils';
import { BexioContactPicker } from './BexioContactPicker';

const CURRENCIES = ['CHF', 'EUR', 'USD'];

function parsePaymentTermsDays(value) {
  const raw = parseInt(String(value), 10);
  return Number.isFinite(raw) && raw >= 0 ? raw : 30;
}

function parseNonNegInt(value, fallback = 0) {
  const raw = parseInt(String(value), 10);
  return Number.isFinite(raw) && raw >= 0 ? raw : fallback;
}

function parsePercent(value, fallback = 0) {
  const raw = parseFloat(String(value));
  if (!Number.isFinite(raw)) return fallback;
  return Math.min(100, Math.max(0, raw));
}

function parseOptionalNonNegFloat(value) {
  if (value === '' || value === null || value === undefined) return null;
  const raw = parseFloat(String(value));
  if (!Number.isFinite(raw)) return null;
  return Math.max(0, raw);
}

function SummaryRow({ label, value, color, testId, tip }) {
  const labelEl = <span className="text-xs text-gray-500">{label}</span>;
  return (
    <div className="flex justify-between items-baseline">
      {tip ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild><span className="text-xs text-gray-500 cursor-default">{label}</span></TooltipTrigger>
            <TooltipContent side="right" className="text-xs max-w-52">{tip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : labelEl}
      <span className={cn("font-mono text-sm font-semibold tabular-nums", color)} data-testid={testId}>
        {value}
      </span>
    </div>
  );
}

export function LeftPanel({ deal, onUpdate, loading, presentationCurrency, fxRates }) {
  const [localState, setLocalState] = useState({
    project_name: '',
    client_name: '',
    client_budget_value: 0,
    client_budget_currency: 'CHF',
    target_price_value: 0,
    target_price_currency: 'CHF',
    margin_target_pct: 30,
    deal_discount_type: 'none',
    deal_discount_value: 0,
    deal_closure_date: '',
    service_rendered_date: '',
    payment_terms_days: 30,
    signature_date: '',
    upfront_amount_chf: '',
    upfront_percentage: 0,
    upfront_payment_delay_days: 0,
    upfront_payment_terms_days: 0,
    delivery_date: '',
    final_invoice_delay_days: 0,
    final_payment_terms_days: 30,
  });

  useEffect(() => {
    if (deal) {
      setLocalState({
        project_name: deal.project_name || '',
        client_name: deal.client_name || '',
        client_budget_value: deal.client_budget?.value || 0,
        client_budget_currency: deal.client_budget?.currency || 'CHF',
        target_price_value: deal.target_price?.value || 0,
        target_price_currency: deal.target_price?.currency || 'CHF',
        margin_target_pct: deal.margin_target_pct ?? 30,
        deal_discount_type: deal.deal_discount_type || 'none',
        deal_discount_value: deal.deal_discount_value ?? 0,
        deal_closure_date: deal.deal_closure_date
          ? String(deal.deal_closure_date).slice(0, 10)
          : '',
        service_rendered_date: deal.service_rendered_date
          ? String(deal.service_rendered_date).slice(0, 10)
          : '',
        payment_terms_days: deal.payment_terms_days ?? 30,
        signature_date: deal.signature_date ? String(deal.signature_date).slice(0, 10) : '',
        upfront_amount_chf: deal.upfront_amount_chf ?? '',
        upfront_percentage: deal.upfront_percentage ?? 0,
        upfront_payment_delay_days: deal.upfront_payment_delay_days ?? 0,
        upfront_payment_terms_days: deal.upfront_payment_terms_days ?? 0,
        delivery_date: deal.delivery_date ? String(deal.delivery_date).slice(0, 10) : '',
        final_invoice_delay_days: deal.final_invoice_delay_days ?? 0,
        final_payment_terms_days: deal.final_payment_terms_days ?? 30,
      });
    }
  }, [deal]);

  const buildUpdatePayload = useCallback((overrides = {}) => {
    const ecd = localState.deal_closure_date?.trim();
    const srd = localState.service_rendered_date?.trim();
    const signatureDate = localState.signature_date?.trim();
    const deliveryDate = localState.delivery_date?.trim();
    return {
      project_name: localState.project_name,
      client_name: localState.client_name,
      bexio_contact_id: deal?.bexio_contact_id ?? null,
      client_budget: {
        value: parseFloat(localState.client_budget_value) || 0,
        currency: localState.client_budget_currency,
      },
      target_price: {
        value: parseFloat(localState.target_price_value) || 0,
        currency: localState.target_price_currency,
      },
      margin_target_pct: parseFloat(localState.margin_target_pct) || 30,
      deal_discount_type: localState.deal_discount_type || 'none',
      deal_discount_value: parseFloat(localState.deal_discount_value) || 0,
      core_currency: deal?.core_currency || 'CHF',
      fx_mode: deal?.fx_mode,
      stage: deal?.stage || 'Demand',
      deal_closure_date: ecd ? ecd : null,
      service_rendered_date: srd ? srd : null,
      payment_terms_days: parsePaymentTermsDays(localState.payment_terms_days),
      signature_date: signatureDate ? signatureDate : null,
      upfront_amount_chf: parseOptionalNonNegFloat(localState.upfront_amount_chf),
      upfront_percentage: parsePercent(localState.upfront_percentage, 0),
      upfront_payment_delay_days: parseNonNegInt(localState.upfront_payment_delay_days, 0),
      upfront_payment_terms_days: parseNonNegInt(localState.upfront_payment_terms_days, 0),
      delivery_date: deliveryDate ? deliveryDate : null,
      final_invoice_delay_days: parseNonNegInt(localState.final_invoice_delay_days, 0),
      final_payment_terms_days: parseNonNegInt(localState.final_payment_terms_days, 30),
      ...overrides,
    };
  }, [deal, localState]);

  const commitChanges = useCallback(() => {
    if (!deal) return;
    const nextClientName = String(localState.client_name || '').trim();
    const currentClientName = String(deal.client_name || '').trim();
    const nextBexioContactId = nextClientName !== currentClientName ? null : (deal.bexio_contact_id ?? null);
    onUpdate(buildUpdatePayload({
      client_name: localState.client_name,
      bexio_contact_id: nextBexioContactId,
    }));
  }, [buildUpdatePayload, deal, localState.client_name, onUpdate]);

  const handleChange = (field, value) => {
    setLocalState(prev => ({ ...prev, [field]: value }));
  };

  const handleBlur = () => { commitChanges(); };
  const handleKeyDown = (e) => { if (e.key === 'Enter') e.target.blur(); };

  if (!deal) {
    return (
      <div className="deal-sidebar w-full min-w-0 shrink-0 border-b border-gray-200 bg-white p-4 flex flex-col lg:w-[280px] lg:border-b-0 lg:border-r">
        <p className="text-sm text-gray-500">No deal selected</p>
      </div>
    );
  }

  // Compute core currency equivalents for budget and target (for internal block display)
  const fx = fxRates || deal.fx_snapshot || { EUR_CHF: 1, USD_CHF: 1 };
  const cc = deal.core_currency || 'CHF';

  // Budget in core currency
  const budgetCore = (() => {
    const v = deal.client_budget?.value || 0;
    const c = deal.client_budget?.currency || 'CHF';
    if (c === cc) return v;
    // Route: source -> CHF -> core
    const toChf = (amt, cur) => {
      if (cur === 'CHF') return amt;
      if (cur === 'EUR') return amt * (fx.EUR_CHF || 1);
      if (cur === 'USD') return amt * (fx.USD_CHF || 1);
      return amt;
    };
    const fromChf = (amt, target) => {
      if (target === 'CHF') return amt;
      if (target === 'EUR') return amt / (fx.EUR_CHF || 1);
      if (target === 'USD') return amt / (fx.USD_CHF || 1);
      return amt;
    };
    return fromChf(toChf(v, c), cc);
  })();

  // Target in core currency
  const targetCore = (() => {
    const v = deal.target_price?.value || 0;
    const c = deal.target_price?.currency || 'CHF';
    if (c === cc) return v;
    const toChf = (amt, cur) => {
      if (cur === 'CHF') return amt;
      if (cur === 'EUR') return amt * (fx.EUR_CHF || 1);
      if (cur === 'USD') return amt * (fx.USD_CHF || 1);
      return amt;
    };
    const fromChf = (amt, target) => {
      if (target === 'CHF') return amt;
      if (target === 'EUR') return amt / (fx.EUR_CHF || 1);
      if (target === 'USD') return amt / (fx.USD_CHF || 1);
      return amt;
    };
    return fromChf(toChf(v, c), cc);
  })();

  const showClientView = presentationCurrency !== cc;
  const pc = presentationCurrency;

  // Convert core currency values to presentation currency
  const toPC = (baseVal) => chfToPresentation(baseVal, pc, fx, cc);

  return (
    <div className="deal-sidebar w-full min-w-0 shrink-0 border-b border-gray-200 bg-white p-4 flex flex-col gap-3 overflow-y-auto lg:w-[280px] lg:border-b-0 lg:border-r">
      {/* Project Name */}
      <div className="flex flex-col gap-1">
        <label className="panel-label" data-testid="project-label">PROJECT</label>
        <Input
          value={localState.project_name}
          onChange={(e) => handleChange('project_name', e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={loading}
          className="h-8 text-[12px] font-medium"
          data-testid="project-name-input"
        />
      </div>

      {/* Client Name */}
      <div className="flex flex-col gap-1">
        <label className="panel-label" data-testid="client-label">CLIENT</label>
        <div className="flex gap-1.5">
          <Input
            value={localState.client_name}
            onChange={(e) => handleChange('client_name', e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={loading}
            className="h-8 flex-1 min-w-0 text-[12px]"
            data-testid="client-name-input"
          />
          <BexioContactPicker
            disabled={loading}
            onPick={(contact) => {
              const name = contact?.label || '';
              setLocalState((prev) => ({ ...prev, client_name: name }));
              onUpdate(buildUpdatePayload({
                client_name: name,
                bexio_contact_id: contact?.id ?? null,
              }));
            }}
          />
        </div>
        {deal.bexio_contact_id ? (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
              Linked to Bexio contact #{deal.bexio_contact_id}
            </span>
            <button
              type="button"
              onClick={() => onUpdate(buildUpdatePayload({ bexio_contact_id: null }))}
              disabled={loading}
              className="text-[11px] font-medium text-gray-600 underline underline-offset-2 disabled:opacity-50"
            >
              Unlink
            </button>
            <span className="text-[11px] text-gray-500">
              Pick another contact or edit the client name to change it.
            </span>
          </div>
        ) : null}
      </div>

      {/* Client Budget */}
      <div className="flex flex-col gap-1">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <label className="panel-label cursor-default" data-testid="budget-label">CLIENT BUDGET</label>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs max-w-52">Max the client will pay. Converted to {cc} for gap analysis.</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className="flex gap-2">
          <Input
            type="number"
            value={localState.client_budget_value}
            onChange={(e) => handleChange('client_budget_value', e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={loading}
            className="h-8 font-mono flex-1 text-[12px]"
            data-testid="client-budget-input"
          />
          <Select
            value={localState.client_budget_currency}
            onValueChange={(v) => {
              handleChange('client_budget_currency', v);
              // Pass override — commitChanges closes over stale localState
              onUpdate(buildUpdatePayload({
                client_budget: {
                  value: parseFloat(localState.client_budget_value) || 0,
                  currency: v,
                },
              }));
            }}
            disabled={loading}
          >
            <SelectTrigger className="w-20 h-8 text-[12px]" data-testid="client-budget-currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Target Price */}
      <div className="flex flex-col gap-1">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <label className="panel-label cursor-default" data-testid="target-label">TARGET PRICE</label>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs max-w-52">Your desired selling price. Used for gap calculation.</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className="flex gap-2">
          <Input
            type="number"
            value={localState.target_price_value}
            onChange={(e) => handleChange('target_price_value', e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={loading}
            className="h-8 font-mono flex-1 text-[12px]"
            data-testid="target-price-input"
          />
          <Select
            value={localState.target_price_currency}
            onValueChange={(v) => {
              handleChange('target_price_currency', v);
              // Pass override — commitChanges closes over stale localState
              onUpdate(buildUpdatePayload({
                target_price: {
                  value: parseFloat(localState.target_price_value) || 0,
                  currency: v,
                },
              }));
            }}
            disabled={loading}
          >
            <SelectTrigger className="w-20 h-8 text-[12px]" data-testid="target-price-currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Deal Closure Date (forecast timing SSOT) */}
      <div className="flex flex-col gap-1">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <label className="panel-label cursor-default" data-testid="deal-closure-date-label">DEAL CLOSURE DATE</label>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs max-w-52">
              Date the deal is officially signed off. This is the single source of truth for forecast timing.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Input
          type="date"
          value={localState.deal_closure_date}
          onChange={(e) => {
            const v = e.target.value;
            handleChange('deal_closure_date', v);
            if (!deal) return;
            onUpdate({
              project_name: localState.project_name,
              client_name: localState.client_name,
              client_budget: {
                value: parseFloat(localState.client_budget_value) || 0,
                currency: localState.client_budget_currency,
              },
              target_price: {
                value: parseFloat(localState.target_price_value) || 0,
                currency: localState.target_price_currency,
              },
              margin_target_pct: parseFloat(localState.margin_target_pct) || 30,
              core_currency: deal.core_currency || 'CHF',
              fx_mode: deal.fx_mode,
              stage: deal.stage || 'Demand',
              deal_closure_date: v || null,
              service_rendered_date: localState.service_rendered_date?.trim()
                ? localState.service_rendered_date.trim()
                : null,
              payment_terms_days: parsePaymentTermsDays(localState.payment_terms_days),
              signature_date: localState.signature_date?.trim() ? localState.signature_date.trim() : null,
              upfront_amount_chf: parseOptionalNonNegFloat(localState.upfront_amount_chf),
              upfront_percentage: parsePercent(localState.upfront_percentage, 0),
              upfront_payment_delay_days: parseNonNegInt(localState.upfront_payment_delay_days, 0),
              upfront_payment_terms_days: parseNonNegInt(localState.upfront_payment_terms_days, 0),
              delivery_date: localState.delivery_date?.trim() ? localState.delivery_date.trim() : null,
              final_invoice_delay_days: parseNonNegInt(localState.final_invoice_delay_days, 0),
              final_payment_terms_days: parseNonNegInt(localState.final_payment_terms_days, 30),
            });
          }}
          disabled={loading}
          className="h-8 font-mono text-[12px]"
          data-testid="deal-closure-date-input"
        />
      </div>

      {/* Service rendered date — AR timetable for uninvoiced open balance */}
      <div className="flex flex-col gap-1">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <label className="panel-label cursor-default" data-testid="service-rendered-date-label">SERVICE RENDERED DATE</label>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs max-w-56">
              Expected date the full contract is delivered and ready to bill at 100%. The dashed uninvoiced chip in Receivables-by-month is placed in this month when set (even if other invoices exist on the deal — those use their own due months). If empty, that chip follows the deal portfolio due date.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Input
          type="date"
          value={localState.service_rendered_date}
          onChange={(e) => {
            const v = e.target.value;
            handleChange('service_rendered_date', v);
            if (!deal) return;
            onUpdate({
              project_name: localState.project_name,
              client_name: localState.client_name,
              client_budget: {
                value: parseFloat(localState.client_budget_value) || 0,
                currency: localState.client_budget_currency,
              },
              target_price: {
                value: parseFloat(localState.target_price_value) || 0,
                currency: localState.target_price_currency,
              },
              margin_target_pct: parseFloat(localState.margin_target_pct) || 30,
              core_currency: deal.core_currency || 'CHF',
              fx_mode: deal.fx_mode,
              stage: deal.stage || 'Demand',
              deal_closure_date: localState.deal_closure_date?.trim()
                ? localState.deal_closure_date.trim()
                : null,
              service_rendered_date: v || null,
              payment_terms_days: parsePaymentTermsDays(localState.payment_terms_days),
              signature_date: localState.signature_date?.trim() ? localState.signature_date.trim() : null,
              upfront_amount_chf: parseOptionalNonNegFloat(localState.upfront_amount_chf),
              upfront_percentage: parsePercent(localState.upfront_percentage, 0),
              upfront_payment_delay_days: parseNonNegInt(localState.upfront_payment_delay_days, 0),
              upfront_payment_terms_days: parseNonNegInt(localState.upfront_payment_terms_days, 0),
              delivery_date: localState.delivery_date?.trim() ? localState.delivery_date.trim() : null,
              final_invoice_delay_days: parseNonNegInt(localState.final_invoice_delay_days, 0),
              final_payment_terms_days: parseNonNegInt(localState.final_payment_terms_days, 30),
            });
          }}
          disabled={loading}
          className="h-8 font-mono text-[12px]"
          data-testid="service-rendered-date-input"
        />
      </div>

      {/* Margin Target */}
      <div className="flex flex-col gap-1">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <label className="panel-label cursor-default" data-testid="margin-target-label">MARGIN TARGET %</label>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs max-w-52">Minimum acceptable margin. GO if above, FIX if below, KILL if under 15%.</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={localState.margin_target_pct}
            onChange={(e) => handleChange('margin_target_pct', e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={loading}
            className="h-8 font-mono w-20 text-[12px]"
            data-testid="margin-target-input"
          />
          <span className="text-gray-500">%</span>
        </div>
        <p className="text-xs text-gray-400 mt-1">GO &ge; target &middot; KILL &lt; 15%</p>
      </div>

      {/* Deal-level discount */}
      <div className="flex flex-col gap-1">
        <label className="panel-label">DEAL DISCOUNT</label>
        <div className="flex items-center gap-2">
          <Select
            value={localState.deal_discount_type}
            onValueChange={(v) => {
              handleChange('deal_discount_type', v);
              // Pass override — commitChanges closes over stale localState,
              // so setTimeout(commitChanges) would re-save the previous type
              // (e.g. selecting "%" still persisted as "none").
              onUpdate(buildUpdatePayload({ deal_discount_type: v }));
            }}
            disabled={loading}
          >
            <SelectTrigger className="h-8 w-24 text-[12px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="percent">%</SelectItem>
              <SelectItem value="amount">Amount</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="number"
            value={localState.deal_discount_value}
            onChange={(e) => handleChange('deal_discount_value', e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={loading || localState.deal_discount_type === 'none'}
            className="h-8 font-mono flex-1 text-[12px]"
          />
        </div>
      </div>

      {/* Core Currency */}
      <div className="flex flex-col gap-1">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <label className="panel-label cursor-default" data-testid="core-currency-label">CORE CURRENCY</label>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs max-w-52">Computational base for this deal. Revenue, cost, margins, gaps, and verdicts are all computed in this currency. Presentation and vendor currencies are display-only projections.</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Select
          value={deal.core_currency || 'CHF'}
          onValueChange={(v) => {
            onUpdate({
              project_name: localState.project_name,
              client_name: localState.client_name,
              client_budget: {
                value: parseFloat(localState.client_budget_value) || 0,
                currency: localState.client_budget_currency,
              },
              target_price: {
                value: parseFloat(localState.target_price_value) || 0,
                currency: localState.target_price_currency,
              },
              margin_target_pct: parseFloat(localState.margin_target_pct) || 30,
              core_currency: v,
              fx_mode: deal.fx_mode,
              stage: deal.stage || 'Demand',
              deal_closure_date: localState.deal_closure_date?.trim()
                ? localState.deal_closure_date.trim()
                : null,
              service_rendered_date: localState.service_rendered_date?.trim()
                ? localState.service_rendered_date.trim()
                : null,
              payment_terms_days: parsePaymentTermsDays(localState.payment_terms_days),
              signature_date: localState.signature_date?.trim() ? localState.signature_date.trim() : null,
              upfront_amount_chf: parseOptionalNonNegFloat(localState.upfront_amount_chf),
              upfront_percentage: parsePercent(localState.upfront_percentage, 0),
              upfront_payment_delay_days: parseNonNegInt(localState.upfront_payment_delay_days, 0),
              upfront_payment_terms_days: parseNonNegInt(localState.upfront_payment_terms_days, 0),
              delivery_date: localState.delivery_date?.trim() ? localState.delivery_date.trim() : null,
              final_invoice_delay_days: parseNonNegInt(localState.final_invoice_delay_days, 0),
              final_payment_terms_days: parseNonNegInt(localState.final_payment_terms_days, 30),
            });
          }}
          disabled={loading}
        >
          <SelectTrigger className="w-24 h-8 font-mono font-semibold text-[12px]" data-testid="core-currency-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CURRENCIES.map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Internal Decision Layer */}
      <div className="border-t border-gray-200 pt-3">
        <label className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-3" data-testid="internal-block-label">
          Internal ({deal.core_currency || 'CHF'})
        </label>
        <div className="flex flex-col gap-2 border border-gray-200 rounded-md p-3" data-testid="internal-chf-block">
          <SummaryRow
            label="Deal Value"
            value={formatNumber(deal.total_revenue_chf)}
            color="text-gray-900"
            testId="internal-deal-value"
            tip={`Total revenue in ${cc} — sum of all line items converted at current FX rates`}
          />
          <SummaryRow
            label="Client Budget"
            value={formatNumber(budgetCore)}
            color="text-gray-700"
            testId="internal-budget-chf"
            tip={`Client budget converted to ${cc} — used for gap analysis`}
          />
          <SummaryRow
            label="Gap vs Budget"
            value={formatGap(deal.gap_vs_client_chf)}
            color={getGapColor(deal.gap_vs_client_chf)}
            testId="internal-gap-budget"
            tip={`Revenue minus Client Budget in ${cc}. Negative = under budget (good)`}
          />
          <div className="border-t border-gray-200 my-0.5" />
          <SummaryRow
            label="Target Price"
            value={formatNumber(targetCore)}
            color="text-gray-700"
            testId="internal-target-chf"
            tip={`Your target selling price converted to ${cc}`}
          />
          <SummaryRow
            label="Gap vs Target"
            value={formatGap(deal.gap_vs_target_chf)}
            color={getGapColor(deal.gap_vs_target_chf)}
            testId="internal-gap-target"
            tip={`Revenue minus Target Price in ${cc}. Shows how far you are from desired price`}
          />
        </div>
      </div>

      {/* ═══════════ CLIENT — Presentation Layer ═══════════ */}
      {/* All values derived from CHF via FX. No independent computation. */}
      <div className={cn(
        "border-t pt-3",
        showClientView ? "border-blue-300" : "border-gray-300"
      )}>
        <label 
          className={cn(
            "text-xs font-bold uppercase tracking-widest block mb-3",
            showClientView ? "text-blue-600" : "text-gray-600"
          )} 
          data-testid="client-block-label"
        >
          Client ({pc})
        </label>
        <div className={cn(
          "flex flex-col gap-2 rounded-md p-3",
          showClientView ? "bg-blue-50 border-2 border-blue-200 shadow-sm" : "bg-gray-50 border border-gray-200"
        )} data-testid="client-view-block">
          <SummaryRow
            label="Deal Value"
            value={formatNumber(toPC(deal.total_revenue_chf))}
            color={showClientView ? "text-blue-900" : "text-gray-900"}
            testId="client-deal-value"
          />
          <SummaryRow
            label="Client Budget"
            value={formatNumber(toPC(budgetCore))}
            color={showClientView ? "text-blue-700" : "text-gray-700"}
            testId="client-budget-display"
          />
          <SummaryRow
            label="Gap vs Budget"
            value={formatGap(toPC(deal.gap_vs_client_chf))}
            color={getGapColor(deal.gap_vs_client_chf)}
            testId="client-gap-budget"
          />
          <div className={cn("border-t my-0.5", showClientView ? "border-blue-100" : "border-gray-200")} />
          <SummaryRow
            label="Target Price"
            value={formatNumber(toPC(targetCore))}
            color={showClientView ? "text-blue-700" : "text-gray-700"}
            testId="client-target-display"
          />
          <SummaryRow
            label="Gap vs Target"
            value={formatGap(toPC(deal.gap_vs_target_chf))}
            color={getGapColor(deal.gap_vs_target_chf)}
            testId="client-gap-target"
          />
        </div>
      </div>
      <div className="h-4 shrink-0" aria-hidden="true" />
    </div>
  );
}
